import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Eye, LockKeyhole, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { defaultContent, mergeContent, useEventContent } from "@/context/EventContentContext";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";

const sections = [
  { id: "general", label: "General" },
  { id: "evento", label: "Evento" },
  { id: "programa", label: "Programa" },
  { id: "anuncios", label: "Anuncios" },
  { id: "chefs", label: "Chefs" },
  { id: "menu", label: "Menu" },
  { id: "galeria", label: "Galeria" },
  { id: "faq", label: "FAQ" },
  { id: "boletos", label: "Boletos" },
  { id: "descuentos", label: "Descuentos" },
  { id: "staff", label: "Staff" },
];

const staffSections = [
  { id: "committees", label: "Comites" },
  { id: "checklist", label: "Checklist" },
  { id: "runOfShow", label: "Run of Show" },
  { id: "contacts", label: "Contactos" },
  { id: "suppliers", label: "Proveedores" },
  { id: "incidents", label: "Incidencias" },
  { id: "documents", label: "Documentos" },
];

const emptyProgram = { time: "", title: "", description: "" };
const emptyChef = { name: "", role: "", bio: "", participation: "", photo: "" };
const emptyDish = {
  course: "",
  name: "",
  description: "",
  ingredients: [],
  chef: "",
  pairing: "",
  image: "",
};
const emptyFaq = { q: "", a: "" };
const newAnnouncement = () => ({
  title: "",
  body: "",
  category: "Comunicado",
  date: new Date().toISOString(),
  important: false,
});
const defaultStaffData = {
  committees: [],
  checklist: [],
  runOfShow: [],
  contacts: [],
  suppliers: [],
  incidents: [],
  documents: [],
};
const staffTemplates = {
  committees: { name: "", leader: "", members: [], tasks: "", status: "Pendiente", notes: "" },
  checklist: { phase: "antes", task: "", responsible: "", deadline: "", priority: "Media", status: "Pendiente", notes: "", done: false },
  runOfShow: { time: "", activity: "", responsible: "", area: "", notes: "", status: "Programado" },
  contacts: { name: "", role: "", committee: "", phone: "", email: "", notes: "" },
  suppliers: { name: "", service: "", contact: "", estimated_cost: "", payment_status: "Pendiente", notes: "" },
  incidents: () => ({ type: "Operativo", time: new Date().toISOString(), reporter: "", description: "", urgency: "Media", status: "Abierto" }),
  documents: { title: "", type: "PDF", url: "", description: "" },
};

const staffEditorConfig = {
  committees: {
    title: "Comites del staff",
    addLabel: "Agregar comite",
    getTitle: (item) => item.name || "Nuevo comite",
    fields: [
      { key: "name", label: "Nombre del comite" },
      { key: "leader", label: "Lider" },
      { key: "members", label: "Integrantes separados por coma", kind: "list" },
      { key: "status", label: "Estado" },
      { key: "tasks", label: "Tareas principales", multiline: true },
      { key: "notes", label: "Notas", multiline: true },
    ],
  },
  checklist: {
    title: "Checklist operativa",
    addLabel: "Agregar tarea",
    getTitle: (item) => item.task || "Nueva tarea",
    fields: [
      { key: "phase", label: "Fase: antes / durante / despues" },
      { key: "task", label: "Tarea" },
      { key: "responsible", label: "Responsable" },
      { key: "deadline", label: "Fecha u hora limite" },
      { key: "priority", label: "Prioridad" },
      { key: "status", label: "Estado" },
      { key: "done", label: "Completada", kind: "checkbox" },
      { key: "notes", label: "Notas", multiline: true },
    ],
  },
  runOfShow: {
    title: "Run of Show interno",
    addLabel: "Agregar momento",
    getTitle: (item) => `${item.time || "Sin hora"} · ${item.activity || "Nueva actividad"}`,
    fields: [
      { key: "time", label: "Hora" },
      { key: "activity", label: "Actividad" },
      { key: "responsible", label: "Responsable" },
      { key: "area", label: "Area" },
      { key: "status", label: "Estado" },
      { key: "notes", label: "Notas", multiline: true },
    ],
  },
  contacts: {
    title: "Directorio de contactos",
    addLabel: "Agregar contacto",
    getTitle: (item) => item.name || "Nuevo contacto",
    fields: [
      { key: "name", label: "Nombre" },
      { key: "role", label: "Rol" },
      { key: "committee", label: "Comite" },
      { key: "phone", label: "Telefono" },
      { key: "email", label: "Correo" },
      { key: "notes", label: "Notas", multiline: true },
    ],
  },
  suppliers: {
    title: "Proveedores",
    addLabel: "Agregar proveedor",
    getTitle: (item) => item.name || "Nuevo proveedor",
    fields: [
      { key: "name", label: "Proveedor" },
      { key: "service", label: "Servicio" },
      { key: "contact", label: "Contacto" },
      { key: "estimated_cost", label: "Costo estimado" },
      { key: "payment_status", label: "Estado de pago" },
      { key: "notes", label: "Notas", multiline: true },
    ],
  },
  incidents: {
    title: "Incidencias",
    addLabel: "Agregar incidencia",
    getTitle: (item) => item.description || "Nueva incidencia",
    fields: [
      { key: "type", label: "Tipo" },
      { key: "time", label: "Fecha/hora ISO" },
      { key: "reporter", label: "Reporta" },
      { key: "urgency", label: "Urgencia" },
      { key: "status", label: "Estado" },
      { key: "description", label: "Descripcion", multiline: true },
    ],
  },
  documents: {
    title: "Documentos",
    addLabel: "Agregar documento",
    getTitle: (item) => item.title || "Nuevo documento",
    fields: [
      { key: "title", label: "Titulo" },
      { key: "type", label: "Tipo" },
      { key: "url", label: "URL del archivo" },
      { key: "description", label: "Descripcion", multiline: true },
    ],
  },
};

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function getPath(obj, path) {
  return path.split(".").reduce((acc, key) => acc?.[key], obj) ?? "";
}

function setPath(obj, path, value) {
  const keys = path.split(".");
  const next = clone(obj);
  let cursor = next;
  keys.slice(0, -1).forEach((key) => {
    if (!cursor[key] || typeof cursor[key] !== "object") cursor[key] = {};
    cursor = cursor[key];
  });
  cursor[keys[keys.length - 1]] = value;
  return next;
}

function Field({ label, value, onChange, multiline = false, type = "text", placeholder = "" }) {
  const inputClass =
    "w-full border border-[var(--brand-border)] bg-white px-3 py-2.5 text-sm outline-none focus:border-[var(--brand-primary)]";

  return (
    <label className="block">
      <span className="overline text-[var(--brand-muted)]">{label}</span>
      {multiline ? (
        <textarea
          className={`${inputClass} mt-2 min-h-[96px] resize-y`}
          value={value || ""}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          className={`${inputClass} mt-2`}
          type={type}
          value={value || ""}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </label>
  );
}

function fileToImageDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer la imagen."));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error("No se pudo procesar la imagen."));
      image.onload = () => {
        const maxSide = 1600;
        const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, width, height);
        const outputType = file.type === "image/png" ? "image/png" : "image/jpeg";
        resolve(canvas.toDataURL(outputType, outputType === "image/png" ? undefined : 0.82));
      };
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function ImageField({ label, value, onChange, fit = "cover" }) {
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await fileToImageDataUrl(file);
      onChange(dataUrl);
      toast.success("Imagen cargada al panel.");
    } catch {
      toast.error("No se pudo cargar la imagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <span className="overline text-[var(--brand-muted)]">{label}</span>
      <div className="grid md:grid-cols-[160px_1fr] gap-4 mt-2 items-start">
        <div className="aspect-[4/3] overflow-hidden bg-[var(--brand-surface-alt)] border border-[var(--brand-border)]">
          {value ? <img src={value} alt="" className={`w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"}`} /> : null}
        </div>
        <div className="space-y-2">
          <input
            className="w-full border border-[var(--brand-border)] bg-white px-3 py-2.5 text-sm outline-none focus:border-[var(--brand-primary)]"
            value={value || ""}
            placeholder="Pega una URL o sube un archivo"
            onChange={(event) => onChange(event.target.value)}
          />
          <input
            className="w-full text-sm"
            type="file"
            accept="image/*"
            disabled={busy}
            onChange={(event) => upload(event.target.files?.[0])}
          />
          <p className="text-xs text-[var(--brand-muted)]">
            Puedes pegar un link o cargar una imagen desde tu computadora. El panel la comprime antes de guardarla.
          </p>
        </div>
      </div>
    </div>
  );
}

function EditorCard({ title, subtitle, children, actions }) {
  return (
    <article className="card-cg p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h3 className="font-serif-cg text-2xl tracking-tight">{title}</h3>
          {subtitle ? <p className="text-sm text-[var(--brand-muted)] mt-1">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </div>
      {children}
    </article>
  );
}

function CheckboxField({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 border border-[var(--brand-border)] bg-white px-3 py-2.5">
      <input type="checkbox" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

export default function AdminPanel() {
  const { refreshContent } = useEventContent();
  const [pin, setPin] = useState(sessionStorage.getItem("cg_admin_pin") || "");
  const [authenticated, setAuthenticated] = useState(sessionStorage.getItem("cg_admin_ok") === "1");
  const [active, setActive] = useState("general");
  const [activeStaffSection, setActiveStaffSection] = useState("committees");
  const [content, setContent] = useState(defaultContent);
  const [staffData, setStaffData] = useState(defaultStaffData);
  const [announcements, setAnnouncements] = useState([]);
  const [loadStatus, setLoadStatus] = useState({ content: false, staff: false, announcements: false });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAdminData = async () => {
      const loadContent = api.get("/content")
        .then((response) => {
          setContent(mergeContent(response.data.content || {}));
          setLoadStatus((current) => ({ ...current, content: true }));
        })
        .catch(() => toast.warning("No se pudo cargar el contenido publico."));

      const loadStaff = api.get("/staff-content")
        .then((response) => {
          setStaffData({ ...defaultStaffData, ...(response.data.staff || {}) });
          setLoadStatus((current) => ({ ...current, staff: true }));
        })
        .catch(() => toast.warning("No se pudo cargar el contenido del staff."));

      const loadAnnouncements = api.get("/announcements")
        .then((response) => {
          setAnnouncements(response.data || []);
          setLoadStatus((current) => ({ ...current, announcements: true }));
        })
        .catch(() => toast.warning("No se pudieron cargar los anuncios."));

      await Promise.all([loadContent, loadStaff, loadAnnouncements]);
      setLoading(false);
    };

    loadAdminData();
  }, []);

  const event = content.event || {};
  const program = content.program || [];
  const chefs = content.chefs || [];
  const menu = content.menu || [];
  const gallery = content.gallery || [];
  const faqs = content.faqs || [];
  const staffCount = Object.values(staffData).reduce((total, list) => total + (Array.isArray(list) ? list.length : 0), 0);

  const counts = useMemo(
    () => ({
      program: program.length,
      chefs: chefs.length,
      menu: menu.length,
      gallery: gallery.length,
      faqs: faqs.length,
      announcements: announcements.length,
      staff: staffCount,
    }),
    [program.length, chefs.length, menu.length, gallery.length, faqs.length, announcements.length, staffCount]
  );

  const allDataLoaded = loadStatus.content && loadStatus.staff && loadStatus.announcements;

  const updateEvent = (path, value) => {
    setContent((current) => ({ ...current, event: setPath(current.event || {}, path, value) }));
  };

  const updateArrayItem = (section, index, key, value) => {
    setContent((current) => {
      const list = clone(current[section] || []);
      list[index] = { ...list[index], [key]: value };
      return { ...current, [section]: list };
    });
  };

  const updateGallery = (index, value) => {
    setContent((current) => {
      const list = clone(current.gallery || []);
      list[index] = value;
      return { ...current, gallery: list };
    });
  };

  const addItem = (section) => {
    const templates = {
      program: emptyProgram,
      chefs: emptyChef,
      menu: emptyDish,
      faqs: emptyFaq,
      gallery: "",
    };
    setContent((current) => ({ ...current, [section]: [...(current[section] || []), clone(templates[section])] }));
  };

  const removeItem = (section, index) => {
    setContent((current) => ({
      ...current,
      [section]: (current[section] || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const moveItem = (section, index, direction) => {
    setContent((current) => {
      const list = clone(current[section] || []);
      const target = index + direction;
      if (target < 0 || target >= list.length) return current;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...current, [section]: list };
    });
  };

  const updateAnnouncement = (index, key, value) => {
    setAnnouncements((current) => {
      const list = clone(current);
      list[index] = { ...list[index], [key]: value };
      return list;
    });
  };

  const addAnnouncement = () => setAnnouncements((current) => [newAnnouncement(), ...current]);
  const removeAnnouncement = (index) => setAnnouncements((current) => current.filter((_, itemIndex) => itemIndex !== index));

  const updateStaffItem = (section, index, key, value) => {
    setStaffData((current) => {
      const list = clone(current[section] || []);
      list[index] = { ...list[index], [key]: value };
      return { ...current, [section]: list };
    });
  };

  const addStaffItem = (section) => {
    const template = staffTemplates[section];
    const item = typeof template === "function" ? template() : clone(template);
    setStaffData((current) => ({ ...current, [section]: [...(current[section] || []), item] }));
  };

  const removeStaffItem = (section, index) => {
    setStaffData((current) => ({
      ...current,
      [section]: (current[section] || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const moveStaffItem = (section, index, direction) => {
    setStaffData((current) => {
      const list = clone(current[section] || []);
      const target = index + direction;
      if (target < 0 || target >= list.length) return current;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...current, [section]: list };
    });
  };

  const login = async (event) => {
    event.preventDefault();
    try {
      await api.post("/admin/login", { pin });
      sessionStorage.setItem("cg_admin_pin", pin);
      sessionStorage.setItem("cg_admin_ok", "1");
      setAuthenticated(true);
      toast.success("Acceso de admin activado.");
    } catch {
      toast.error("PIN incorrecto.");
    }
  };

  const save = async () => {
    if (!allDataLoaded) {
      toast.error("No se guardo nada porque alguna seccion no pudo cargarse. Recarga la pagina.");
      return;
    }
    setSaving(true);
    try {
      await api.put(
        "/content",
        { content },
        {
          headers: { "x-admin-pin": pin },
        }
      );
      await api.put(
        "/staff-content",
        { staff: staffData },
        {
          headers: { "x-admin-pin": pin },
        }
      );
      await api.put(
        "/announcements",
        { announcements },
        {
          headers: { "x-admin-pin": pin },
        }
      );
      await refreshContent();
      toast.success("Cambios guardados y publicados.");
    } catch {
      toast.error("No se pudo guardar. Revisa el PIN o el backend.");
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("cg_admin_pin");
    sessionStorage.removeItem("cg_admin_ok");
    setAuthenticated(false);
  };

  if (!authenticated) {
    return (
      <div className="section-pad min-h-[75vh] flex items-center">
        <div className="container-cg max-w-xl">
          <div className="card-cg p-8 md:p-10">
            <LockKeyhole className="text-[var(--brand-primary)]" size={28} />
            <div className="overline text-[var(--brand-primary)] mt-5">Panel visual</div>
            <h1 className="font-serif-cg text-4xl tracking-tight mt-2">Admin Cuchara Grande</h1>
            <p className="text-sm text-[var(--brand-muted)] mt-3 leading-relaxed">
              Ingresa el PIN de administrador para editar y publicar el contenido del sitio.
            </p>
            <form onSubmit={login} className="mt-7 space-y-4">
              <Field label="PIN de administrador" value={pin} type="password" onChange={setPin} />
              <button className="btn-primary-cg w-full justify-center" type="submit">
                Entrar al panel
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="section-pad">
      <div className="container-cg">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline text-[var(--brand-primary)]">Editor visual</div>
            <h1 className="font-serif-cg text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
              Administrar sitio
            </h1>
            <p className="max-w-2xl mt-4 text-[var(--brand-muted)]">
              Edita marca, textos, programa, anuncios, boletos, redes sociales y portal del staff sin tocar codigo.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/" className="btn-secondary-cg">
              <Eye size={14} /> Vista publica
            </Link>
            <button onClick={logout} className="btn-secondary-cg" type="button">
              Salir
            </button>
            <button onClick={save} disabled={saving || loading || !allDataLoaded} className="btn-primary-cg" type="button">
              <Save size={14} /> {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mt-10">
          <div className="card-cg p-4">
            <div className="overline text-[var(--brand-muted)]">Programa</div>
            <div className="font-serif-cg text-3xl mt-1">{counts.program}</div>
          </div>
          <div className="card-cg p-4">
            <div className="overline text-[var(--brand-muted)]">Chefs</div>
            <div className="font-serif-cg text-3xl mt-1">{counts.chefs}</div>
          </div>
          <div className="card-cg p-4">
            <div className="overline text-[var(--brand-muted)]">Platillos</div>
            <div className="font-serif-cg text-3xl mt-1">{counts.menu}</div>
          </div>
          <div className="card-cg p-4">
            <div className="overline text-[var(--brand-muted)]">Fotos</div>
            <div className="font-serif-cg text-3xl mt-1">{counts.gallery}</div>
          </div>
          <div className="card-cg p-4">
            <div className="overline text-[var(--brand-muted)]">FAQ</div>
            <div className="font-serif-cg text-3xl mt-1">{counts.faqs}</div>
          </div>
          <div className="card-cg p-4">
            <div className="overline text-[var(--brand-muted)]">Anuncios</div>
            <div className="font-serif-cg text-3xl mt-1">{counts.announcements}</div>
          </div>
          <div className="card-cg p-4">
            <div className="overline text-[var(--brand-muted)]">Staff</div>
            <div className="font-serif-cg text-3xl mt-1">{counts.staff}</div>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto mt-8 pb-2">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => setActive(section.id)}
              className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                active === section.id
                  ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                  : "bg-white text-[var(--brand-ink)] border-[var(--brand-border)]"
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-5">
          {active === "general" && (
            <EditorCard title="Informacion general" subtitle="Datos principales que aparecen en home, evento, ubicacion y staff.">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Nombre" value={event.name} onChange={(value) => updateEvent("name", value)} />
                <Field label="Edicion" value={event.edition} onChange={(value) => updateEvent("edition", value)} />
                <Field label="Subtitulo" value={event.subtitle} onChange={(value) => updateEvent("subtitle", value)} />
                <Field label="Fecha" value={event.date} onChange={(value) => updateEvent("date", value)} />
                <Field label="Horario" value={event.time} onChange={(value) => updateEvent("time", value)} />
                <Field label="Sede" value={event.venue} onChange={(value) => updateEvent("venue", value)} />
                <Field label="Ciudad" value={event.city} onChange={(value) => updateEvent("city", value)} />
                <Field label="Tipo de evento" value={event.type} onChange={(value) => updateEvent("type", value)} />
                <Field label="Vestimenta" value={event.dressCode} onChange={(value) => updateEvent("dressCode", value)} />
                <Field label="Estado" value={event.status} onChange={(value) => updateEvent("status", value)} />
                <Field label="Invitados estimados" value={event.expectedGuests} type="number" onChange={(value) => updateEvent("expectedGuests", Number(value))} />
                <Field label="Correo" value={event.contactEmail} onChange={(value) => updateEvent("contactEmail", value)} />
                <Field label="Telefono" value={event.contactPhone} onChange={(value) => updateEvent("contactPhone", value)} />
                <Field label="Texto de Instagram" value={getPath(event, "social.instagram")} onChange={(value) => updateEvent("social.instagram", value)} />
                <Field label="Enlace de Instagram" value={getPath(event, "social.instagramUrl")} type="url" placeholder="https://instagram.com/..." onChange={(value) => updateEvent("social.instagramUrl", value)} />
                <Field label="Texto de Facebook" value={getPath(event, "social.facebook")} onChange={(value) => updateEvent("social.facebook", value)} />
                <Field label="Enlace de Facebook" value={getPath(event, "social.facebookUrl")} type="url" placeholder="https://facebook.com/..." onChange={(value) => updateEvent("social.facebookUrl", value)} />
                <Field label="Texto del sitio web" value={getPath(event, "social.website")} onChange={(value) => updateEvent("social.website", value)} />
                <Field label="Enlace del sitio web" value={getPath(event, "social.websiteUrl")} type="url" placeholder="https://..." onChange={(value) => updateEvent("social.websiteUrl", value)} />
                <Field label="Direccion" value={getPath(event, "location.addressLine")} onChange={(value) => updateEvent("location.addressLine", value)} />
                <Field label="Ciudad/estado en mapa" value={getPath(event, "location.cityState")} onChange={(value) => updateEvent("location.cityState", value)} />
                <Field label="URL Google Maps" value={getPath(event, "location.mapsUrl")} onChange={(value) => updateEvent("location.mapsUrl", value)} />
                <div className="md:col-span-2">
                  <ImageField label="Logo principal" value={event.logoImage} fit="contain" onChange={(value) => updateEvent("logoImage", value)} />
                </div>
                <Field label="Texto alternativo del logo" value={event.logoAlt} onChange={(value) => updateEvent("logoAlt", value)} />
                <div />
                <ImageField label="Imagen principal del home" value={event.heroImage} onChange={(value) => updateEvent("heroImage", value)} />
                <ImageField label="Imagen secundaria del home" value={event.secondaryImage} onChange={(value) => updateEvent("secondaryImage", value)} />
                <div className="md:col-span-2 border-t border-[var(--brand-border)] pt-5 mt-2">
                  <div className="overline text-[var(--brand-primary)] mb-4">Boton flotante de boletos</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <CheckboxField label="Mostrar boton de boletos" checked={getPath(event, "ticketButton.enabled")} onChange={(value) => updateEvent("ticketButton.enabled", value)} />
                    <Field label="Texto del boton" value={getPath(event, "ticketButton.label")} onChange={(value) => updateEvent("ticketButton.label", value)} />
                    <div className="md:col-span-2">
                      <Field label="Enlace para venta de boletos" value={getPath(event, "ticketButton.url")} type="url" placeholder="https://..." onChange={(value) => updateEvent("ticketButton.url", value)} />
                    </div>
                  </div>
                </div>
                <Field label="Como llegar" value={getPath(event, "location.directions")} multiline onChange={(value) => updateEvent("location.directions", value)} />
                <Field label="Estacionamiento" value={getPath(event, "location.parking")} multiline onChange={(value) => updateEvent("location.parking", value)} />
                <Field label="Accesos" value={getPath(event, "location.access")} multiline onChange={(value) => updateEvent("location.access", value)} />
                <div className="md:col-span-2">
                  <Field label="Descripcion principal" value={event.tagline} multiline onChange={(value) => updateEvent("tagline", value)} />
                </div>
                <div className="md:col-span-2">
                  <Field label="Titulo de seccion sobre la edicion" value={event.aboutTitle} multiline onChange={(value) => updateEvent("aboutTitle", value)} />
                </div>
                <div className="md:col-span-2">
                  <Field label="Texto de seccion sobre la edicion" value={event.aboutText} multiline onChange={(value) => updateEvent("aboutText", value)} />
                </div>
                <div className="md:col-span-2">
                  <Field label="Texto conmemorativo Leon 450" value={event.commemorativeText} multiline onChange={(value) => updateEvent("commemorativeText", value)} />
                </div>
              </div>
            </EditorCard>
          )}

          {active === "evento" && (
            <>
              <EditorCard
                title="Pagina /evento"
                subtitle="Edita los textos, recuadro lateral e imagen que aparecen especificamente en la ruta /evento."
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Etiqueta superior" value={event.aboutEyebrow} onChange={(value) => updateEvent("aboutEyebrow", value)} />
                  <Field label="Titulo principal de /evento" value={event.aboutTitle} multiline onChange={(value) => updateEvent("aboutTitle", value)} />
                  <div className="md:col-span-2">
                    <Field
                      label="Texto principal de /evento"
                      value={event.aboutText}
                      multiline
                      placeholder="Usa doble salto de linea para separar parrafos."
                      onChange={(value) => updateEvent("aboutText", value)}
                    />
                  </div>
                  <Field label="Etiqueta del recuadro lateral" value={event.aboutCardEyebrow} onChange={(value) => updateEvent("aboutCardEyebrow", value)} />
                  <Field label="Titulo del recuadro lateral" value={event.aboutCardTitle} onChange={(value) => updateEvent("aboutCardTitle", value)} />
                  <div className="md:col-span-2">
                    <Field
                      label="Texto del recuadro lateral"
                      value={event.commemorativeText}
                      multiline
                      onChange={(value) => updateEvent("commemorativeText", value)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <ImageField label="Imagen opcional de /evento" value={event.aboutImage} onChange={(value) => updateEvent("aboutImage", value)} />
                  </div>
                  <Field label="Texto alternativo de la imagen" value={event.aboutImageAlt} onChange={(value) => updateEvent("aboutImageAlt", value)} />
                </div>
              </EditorCard>

              <EditorCard
                title="Datos que aparecen en /evento"
                subtitle="Estos campos tambien se usan en otras secciones, pero los dejamos aqui para que puedas editar toda la pagina /evento desde una sola pestana."
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Fecha" value={event.date} onChange={(value) => updateEvent("date", value)} />
                  <Field label="Horario" value={event.time} onChange={(value) => updateEvent("time", value)} />
                  <Field label="Sede" value={event.venue} onChange={(value) => updateEvent("venue", value)} />
                  <Field label="Ciudad" value={event.city} onChange={(value) => updateEvent("city", value)} />
                  <Field label="Tipo de evento" value={event.type} onChange={(value) => updateEvent("type", value)} />
                  <Field label="Codigo de vestimenta" value={event.dressCode} onChange={(value) => updateEvent("dressCode", value)} />
                  <Field label="Contacto oficial" value={event.contactEmail} onChange={(value) => updateEvent("contactEmail", value)} />
                  <Field label="Telefono" value={event.contactPhone} onChange={(value) => updateEvent("contactPhone", value)} />
                  <Field label="Texto de Instagram" value={getPath(event, "social.instagram")} onChange={(value) => updateEvent("social.instagram", value)} />
                  <Field label="Enlace de Instagram" value={getPath(event, "social.instagramUrl")} onChange={(value) => updateEvent("social.instagramUrl", value)} />
                  <Field label="Texto de Facebook" value={getPath(event, "social.facebook")} onChange={(value) => updateEvent("social.facebook", value)} />
                  <Field label="Enlace de Facebook" value={getPath(event, "social.facebookUrl")} onChange={(value) => updateEvent("social.facebookUrl", value)} />
                </div>
              </EditorCard>
            </>
          )}

          {active === "programa" && (
            <>
              <button type="button" className="btn-primary-cg" onClick={() => addItem("program")}>
                <Plus size={14} /> Agregar momento
              </button>
              {program.map((item, index) => (
                <EditorCard
                  key={index}
                  title={`${item.time || "Sin hora"} · ${item.title || "Nuevo momento"}`}
                  actions={<ItemActions section="program" index={index} moveItem={moveItem} removeItem={removeItem} />}
                >
                  <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Hora" value={item.time} onChange={(value) => updateArrayItem("program", index, "time", value)} />
                    <Field label="Titulo" value={item.title} onChange={(value) => updateArrayItem("program", index, "title", value)} />
                    <Field label="Descripcion" value={item.description} multiline onChange={(value) => updateArrayItem("program", index, "description", value)} />
                  </div>
                </EditorCard>
              ))}
            </>
          )}

          {active === "anuncios" && (
            <>
              <button type="button" className="btn-primary-cg" onClick={addAnnouncement}>
                <Plus size={14} /> Agregar anuncio
              </button>
              {announcements.length === 0 ? (
                <div className="border border-dashed border-[var(--brand-border)] p-6 text-sm text-[var(--brand-muted)]">
                  No hay anuncios publicados.
                </div>
              ) : null}
              {announcements.map((announcement, index) => (
                <EditorCard
                  key={announcement.id || index}
                  title={announcement.title || "Nuevo anuncio"}
                  subtitle={announcement.category}
                  actions={(
                    <button
                      type="button"
                      className="btn-secondary-cg px-3 py-2 text-red-700"
                      onClick={() => removeAnnouncement(index)}
                      aria-label="Eliminar anuncio"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Titulo" value={announcement.title} onChange={(value) => updateAnnouncement(index, "title", value)} />
                    <Field label="Categoria" value={announcement.category} placeholder="Comunicado" onChange={(value) => updateAnnouncement(index, "category", value)} />
                    <Field label="Fecha y hora (ISO)" value={announcement.date} placeholder="2026-08-01T17:00:00Z" onChange={(value) => updateAnnouncement(index, "date", value)} />
                    <CheckboxField label="Marcar como importante" checked={announcement.important} onChange={(value) => updateAnnouncement(index, "important", value)} />
                    <div className="md:col-span-2">
                      <Field label="Contenido" value={announcement.body} multiline onChange={(value) => updateAnnouncement(index, "body", value)} />
                    </div>
                  </div>
                </EditorCard>
              ))}
            </>
          )}

          {active === "chefs" && (
            <>
              <button type="button" className="btn-primary-cg" onClick={() => addItem("chefs")}>
                <Plus size={14} /> Agregar chef
              </button>
              {chefs.map((chef, index) => (
                <EditorCard
                  key={index}
                  title={chef.name || "Nuevo chef"}
                  subtitle={chef.role}
                  actions={<ItemActions section="chefs" index={index} moveItem={moveItem} removeItem={removeItem} />}
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Nombre" value={chef.name} onChange={(value) => updateArrayItem("chefs", index, "name", value)} />
                    <Field label="Rol" value={chef.role} onChange={(value) => updateArrayItem("chefs", index, "role", value)} />
                    <Field label="Participacion" value={chef.participation} onChange={(value) => updateArrayItem("chefs", index, "participation", value)} />
                    <div className="md:col-span-2">
                      <ImageField label="Foto del chef" value={chef.photo} onChange={(value) => updateArrayItem("chefs", index, "photo", value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Biografia" value={chef.bio} multiline onChange={(value) => updateArrayItem("chefs", index, "bio", value)} />
                    </div>
                  </div>
                </EditorCard>
              ))}
            </>
          )}

          {active === "menu" && (
            <>
              <button type="button" className="btn-primary-cg" onClick={() => addItem("menu")}>
                <Plus size={14} /> Agregar platillo
              </button>
              {menu.map((dish, index) => (
                <EditorCard
                  key={index}
                  title={dish.name || "Nuevo platillo"}
                  subtitle={dish.course}
                  actions={<ItemActions section="menu" index={index} moveItem={moveItem} removeItem={removeItem} />}
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Tiempo / categoria" value={dish.course} onChange={(value) => updateArrayItem("menu", index, "course", value)} />
                    <Field label="Nombre del platillo" value={dish.name} onChange={(value) => updateArrayItem("menu", index, "name", value)} />
                    <Field label="Chef" value={dish.chef} onChange={(value) => updateArrayItem("menu", index, "chef", value)} />
                    <Field label="Maridaje" value={dish.pairing} onChange={(value) => updateArrayItem("menu", index, "pairing", value)} />
                    <Field
                      label="Ingredientes separados por coma"
                      value={(dish.ingredients || []).join(", ")}
                      onChange={(value) => updateArrayItem("menu", index, "ingredients", value.split(",").map((item) => item.trim()).filter(Boolean))}
                    />
                    <div className="md:col-span-2">
                      <ImageField label="Imagen del platillo" value={dish.image} onChange={(value) => updateArrayItem("menu", index, "image", value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Descripcion" value={dish.description} multiline onChange={(value) => updateArrayItem("menu", index, "description", value)} />
                    </div>
                  </div>
                </EditorCard>
              ))}
            </>
          )}

          {active === "galeria" && (
            <>
              <button type="button" className="btn-primary-cg" onClick={() => addItem("gallery")}>
                <Plus size={14} /> Agregar foto
              </button>
              {gallery.map((src, index) => (
                <EditorCard
                  key={index}
                  title={`Foto ${index + 1}`}
                  actions={<ItemActions section="gallery" index={index} moveItem={moveItem} removeItem={removeItem} />}
                >
                  <ImageField label="Imagen de galeria" value={src} onChange={(value) => updateGallery(index, value)} />
                </EditorCard>
              ))}
            </>
          )}

          {active === "faq" && (
            <>
              <button type="button" className="btn-primary-cg" onClick={() => addItem("faqs")}>
                <Plus size={14} /> Agregar pregunta
              </button>
              {faqs.map((faq, index) => (
                <EditorCard
                  key={index}
                  title={faq.q || "Nueva pregunta"}
                  actions={<ItemActions section="faqs" index={index} moveItem={moveItem} removeItem={removeItem} />}
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Pregunta" value={faq.q} onChange={(value) => updateArrayItem("faqs", index, "q", value)} />
                    <Field label="Respuesta" value={faq.a} multiline onChange={(value) => updateArrayItem("faqs", index, "a", value)} />
                  </div>
                </EditorCard>
              ))}
            </>
          )}

          {active === "boletos" && <AdminTickets pin={pin} />}

          {active === "descuentos" && <AdminDiscounts pin={pin} />}

          {active === "staff" && (
            <EditorCard
              title="Portal del staff"
              subtitle="Edita la informacion interna que aparece en /staff: equipos, tareas, contactos, proveedores, incidencias y documentos."
            >
              <div className="flex gap-2 overflow-x-auto pb-3">
                {staffSections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveStaffSection(section.id)}
                    className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors shrink-0 ${
                      activeStaffSection === section.id
                        ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                        : "bg-white text-[var(--brand-ink)] border-[var(--brand-border)]"
                    }`}
                  >
                    {section.label}
                  </button>
                ))}
              </div>

              <StaffCollectionEditor
                section={activeStaffSection}
                items={staffData[activeStaffSection] || []}
                updateItem={updateStaffItem}
                addItem={addStaffItem}
                removeItem={removeStaffItem}
                moveItem={moveStaffItem}
              />
            </EditorCard>
          )}
        </div>
      </div>
    </div>
  );
}

function StaffCollectionEditor({ section, items, updateItem, addItem, removeItem, moveItem }) {
  const config = staffEditorConfig[section];
  if (!config) return null;

  const updateField = (index, field, value) => {
    if (field.kind === "list") {
      updateItem(section, index, field.key, value.split(",").map((item) => item.trim()).filter(Boolean));
      return;
    }
    updateItem(section, index, field.key, value);
  };

  return (
    <div className="space-y-5 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif-cg text-2xl tracking-tight">{config.title}</h3>
          <p className="text-sm text-[var(--brand-muted)] mt-1">{items.length} registros cargados.</p>
        </div>
        <button type="button" className="btn-primary-cg" onClick={() => addItem(section)}>
          <Plus size={14} /> {config.addLabel}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--brand-border)] p-6 text-sm text-[var(--brand-muted)]">
          No hay registros en esta seccion. Agrega uno nuevo para publicarlo en el portal del staff.
        </div>
      ) : null}

      {items.map((item, index) => (
        <EditorCard
          key={item.id || index}
          title={config.getTitle(item)}
          actions={<ItemActions section={section} index={index} moveItem={moveItem} removeItem={removeItem} />}
        >
          <div className="grid md:grid-cols-2 gap-4">
            {config.fields.map((field) => {
              if (field.kind === "checkbox") {
                return (
                  <label key={field.key} className="flex items-center gap-3 border border-[var(--brand-border)] bg-white px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={Boolean(item[field.key])}
                      onChange={(event) => updateItem(section, index, field.key, event.target.checked)}
                    />
                    <span className="text-sm">{field.label}</span>
                  </label>
                );
              }

              const value = field.kind === "list" ? (item[field.key] || []).join(", ") : item[field.key];
              return (
                <Field
                  key={field.key}
                  label={field.label}
                  value={value}
                  multiline={field.multiline}
                  onChange={(value) => updateField(index, field, value)}
                />
              );
            })}
          </div>
        </EditorCard>
      ))}
    </div>
  );
}

function ItemActions({ section, index, moveItem, removeItem }) {
  return (
    <>
      <button type="button" className="btn-secondary-cg px-3 py-2" onClick={() => moveItem(section, index, -1)} aria-label="Subir">
        <ArrowUp size={14} />
      </button>
      <button type="button" className="btn-secondary-cg px-3 py-2" onClick={() => moveItem(section, index, 1)} aria-label="Bajar">
        <ArrowDown size={14} />
      </button>
      <button type="button" className="btn-secondary-cg px-3 py-2 text-red-700" onClick={() => removeItem(section, index)} aria-label="Eliminar">
        <Trash2 size={14} />
      </button>
    </>
  );
}
